import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const RELOCATION_ORDER_SEARCH_RESOURCE = "relocation-order" as const;
export const RELOCATION_ORDER_SEARCH_OPERATION = "search" as const;
export const RELOCATION_ORDER_SEARCH_API_PATH =
  `/v1/${RELOCATION_ORDER_SEARCH_RESOURCE}/${RELOCATION_ORDER_SEARCH_OPERATION}` as const;

export type RelocationOrderSearchParameters =
  paths[typeof RELOCATION_ORDER_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type RelocationOrderSearchBody =
  paths[typeof RELOCATION_ORDER_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type RelocationOrderSearchResponse =
  paths[typeof RELOCATION_ORDER_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useRelocationOrderSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<RelocationOrderSearchParameters, RelocationOrderSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<
    RelocationOrderSearchResponse,
    Error,
    RelocationOrderSearchResponse,
    readonly unknown[]
  >;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<RelocationOrderSearchParameters, RelocationOrderSearchBody>>(options);

  const requestParameters = {
    ...(optionsWithDefaults.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaults.parameters),
  };

  return useQuery({
    queryKey: [
      RELOCATION_ORDER_SEARCH_RESOURCE,
      RELOCATION_ORDER_SEARCH_OPERATION,
      { parameters: requestParameters, body: optionsWithDefaults.body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<RelocationOrderSearchResponse>(
        RELOCATION_ORDER_SEARCH_API_PATH,
        optionsWithDefaults.body,
        {
          params: requestParameters,
        },
      );

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
